import { GridColDef } from "@mui/x-data-grid";
import { useEstoquesMedicamentoDispatch, useEstoquesMedicamentoState } from "./estoques-medicamento-context";
import { ACTION_TYPES, initialDataRequest } from "./estoques-medicamento-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { IEstoqueMedicamentoResponse } from "../../../../model/admin-portal/response/estoque-medicamento-response";
import { ITermPaggedRequest } from "../../../../model/admin-portal/request/term-pagged-request";
import { IconButton, Box, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';

export const useEstoquesMedicamentoController = () => {
    const state = useEstoquesMedicamentoState();
    const dispatch = useEstoquesMedicamentoDispatch();
    const { showMessage, showBackDrop } = useBaseLayoutController();

    const loadData = async (searchRequest: ITermPaggedRequest) => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listEstoquesMedicamento(searchRequest);
            dispatch({ type: ACTION_TYPES.setDataPaginatedListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar estoque de medicamentos' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        const newSearch = { ...state.dataSearchRequest, page, pageSize };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const handleOnChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: { ...state.dataSearchRequest, term: e.target.value } });
    };

    const clearFields = async () => {
        const newSearch = { page: 0, pageSize: state.dataSearchRequest.pageSize, term: '' };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const loadLookups = async () => {
        try {
            const orgaos = await AdminPortalAppService.listOrgaos();
            dispatch({ type: ACTION_TYPES.setOrgaosList, payload: orgaos });
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar lista de Órgãos' });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getEstoqueMedicamento(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });
            dispatch({
                type: ACTION_TYPES.setDataRequest, payload: {
                    id: response.id,
                    idOrgao: response.idOrgao ?? null,
                    nomeMedicamento: response.nomeMedicamento,
                    quantidadeEstoque: response.quantidadeEstoque,
                    dtaAtualizacao: response.dtaAtualizacao
                }
            });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar registro de estoque' });
        } finally {
            showBackDrop(false);
        }
    };

    const validFields = (): boolean => {
        const r = state.dataRequest;
        if (!r.nomeMedicamento || r.nomeMedicamento.trim() === '') return false;
        if (r.quantidadeEstoque === null || r.quantidadeEstoque === undefined || r.quantidadeEstoque < 0) return false;
        if (!r.dtaAtualizacao) return false;
        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, [field]: value } });
    };

    const openForm = (row?: IEstoqueMedicamentoResponse) => {
        if (row) {
            getData(row.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateEstoqueMedicamento(state.dataRequest);
            showMessage({
                type: 'success',
                message: state.dataRequest.id !== undefined && state.dataRequest.id !== null ? 'Registro atualizado com sucesso' : 'Registro criado com sucesso'
            });
            closeForm();
            await loadData(state.dataSearchRequest);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar registro de estoque' });
        } finally {
            showBackDrop(false);
        }
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            { field: 'nomeMedicamento', headerName: 'Medicamento', flex: 1.5, minWidth: 220 },
            { field: 'orgaoName', headerName: 'Farmácia/Unidade', flex: 1, minWidth: 180 },
            { field: 'quantidadeEstoque', headerName: 'Qtd. em Estoque', width: 150 },
            {
                field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openForm(params.row); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    };

    return {
        ...state,
        loadData,
        onChangePagination,
        handleOnChangeSearch,
        clearFields,
        loadLookups,
        getData,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        save,
        getColumnsGrid
    };
};
